import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useSessionKey } from "@auth/sessionStore";
import { faPlus, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toDestinationE164 } from "@phone/dialNumber";
import {
  formatPhoneNumber,
  INBOUND_RING_MODES,
  type InboundRingMode,
  MAX_OUTBOUND_LINES,
  type OutboundLineErrors,
  type OutboundLineInput,
  type PhoneSetup,
  PhoneSetupValidationError,
  RING_TIMEOUT_DEFAULT_SECONDS,
  RING_TIMEOUT_MAX_SECONDS,
  RING_TIMEOUT_MIN_SECONDS,
  validateOutboundLines,
} from "@phone/phoneSetupClient";
import {
  useCallerIdVerification,
  useClearCallerId,
  useStartCallerIdVerification,
  useUpdateOutboundConfig,
  useUpdateOutboundLines,
} from "@phone/phoneSetupQueries";
import {
  Button,
  cn,
  ConfirmDialog,
  FieldError,
  IconButton,
  Input,
  SegmentedControl,
  type SegmentedControlOption,
  SettingsCard,
  Swipe,
  Toggle,
} from "@scribenote/brian-design-system";
import { useRef, useState, type ReactNode } from "react";

export const OutboundSettingsCards = ({
  setup,
  afterToggle,
}: {
  setup: PhoneSetup;
  /** Rendered under the outbound calling toggle. */
  afterToggle?: ReactNode;
}) => (
  <>
    <CallRingCard setup={setup} />
    <OutboundCallingCard setup={setup} />
    {afterToggle}
    <OutboundLinesCard setup={setup} />
    <CallerIdCard setup={setup} />
  </>
);

const RING_LABEL: Record<InboundRingMode, string> = {
  clinic_phones: "Clinic phones only",
  clinic_phones_and_computers: "Clinic phones and computers",
  computers_only: "Computers only",
};

const RING_OPTIONS: readonly SegmentedControlOption<InboundRingMode>[] =
  INBOUND_RING_MODES.map((value) => ({ value, label: RING_LABEL[value] }));

const RING_NOTE: Record<InboundRingMode, string> = {
  clinic_phones:
    "Incoming calls ring the clinic phones; no computer rings until someone turns its headset on.",
  clinic_phones_and_computers:
    "Incoming calls ring the clinic phones and every connected computer together; whoever answers first takes the call.",
  computers_only:
    "Incoming calls ring every connected computer and no clinic phone; if no computer is connected, the caller goes straight to the message flow.",
};

/** With computers it also drives `SoftphoneBridge`'s auto-connect. */
const CallRingCard = ({ setup }: { setup: PhoneSetup }) => {
  const update = useUpdateOutboundConfig();
  const mode = setup.inboundRing;
  return (
    <SettingsCard
      title="Where incoming calls ring"
      subtitle="Whether the computers your staff are signed in on ring for a call, alongside the clinic's phones or instead of them."
      data-testid="phone-ring-card"
    >
      <div className="flex flex-col gap-3">
        <SegmentedControl
          aria-label="Where incoming calls ring"
          options={RING_OPTIONS}
          value={mode}
          onValueChange={(value) => {
            if (value !== mode && !update.isPending)
              update.mutate({ inboundRing: value });
          }}
          data-testid="phone-ring-mode"
        />
        <p
          className="text-fine-print text-annotation"
          data-testid="phone-ring-note"
        >
          {RING_NOTE[mode]}
        </p>
        {update.isError && (
          <FieldError data-testid="phone-ring-error">
            Couldn't change that. Try again.
          </FieldError>
        )}
      </div>
    </SettingsCard>
  );
};

/** Off blocks new calls only; a live call is never cut. */
const OutboundCallingCard = ({ setup }: { setup: PhoneSetup }) => {
  const update = useUpdateOutboundConfig();
  return (
    <SettingsCard
      title="Outbound calling"
      subtitle="Let staff ask me to place calls: I ring your line first, then dial the pet owner and connect you."
      data-testid="phone-outbound-card"
    >
      <div className="flex flex-col gap-3">
        <Toggle
          checked={setup.outboundEnabled}
          disabled={update.isPending}
          onCheckedChange={(enabled) =>
            update.mutate({
              enabled,
            })
          }
          label={
            setup.outboundEnabled
              ? "Staff can place calls through Brian"
              : "Staff can't place calls through Brian"
          }
          data-testid="phone-outbound-toggle"
        />
        <Toggle
          checked={setup.outboundComputerDefault}
          disabled={update.isPending}
          onCheckedChange={(outboundComputerDefault) =>
            update.mutate({ outboundComputerDefault })
          }
          label="Always call from the computer's headset"
          data-testid="phone-outbound-computer-default"
        />
        <p
          className="text-fine-print text-annotation"
          data-testid="phone-outbound-computer-default-note"
        >
          {setup.outboundComputerDefault
            ? "On: Call rings this computer's headset, which connects on its own; a line is offered only if the headset can't connect."
            : "Off: staff pick a line each time they press Call."}
        </p>
        {update.isError && (
          <FieldError data-testid="phone-outbound-error">
            Couldn't change that. Try again.
          </FieldError>
        )}
      </div>
    </SettingsCard>
  );
};

/** The key survives removals above it. */
interface LineDraft {
  key: string;
  label: string;
  address: string;
  enabled: boolean;
  ringTimeout: string;
}

const fromSetup = (setup: PhoneSetup): LineDraft[] =>
  setup.outboundLines.map((line, index) => ({
    key: `server-${index}`,
    label: line.label,
    address: line.addressE164,
    enabled: line.enabled,
    ringTimeout: String(line.ringTimeoutSeconds),
  }));

const toInput = (draft: LineDraft): OutboundLineInput => ({
  label: draft.label.trim(),
  address: draft.address.trim(),
  enabled: draft.enabled,
  ringTimeoutSeconds:
    draft.ringTimeout.trim() === "" ? NaN : Number(draft.ringTimeout),
});

const FIELD_OF_LOC: Record<string, keyof OutboundLineErrors> = {
  label: "label",
  address: "address",
  ring_timeout_seconds: "ringTimeoutSeconds",
};

const from422 = (
  error: unknown,
  rowCount: number,
): { rows: OutboundLineErrors[]; general: string | null } => {
  const rows: OutboundLineErrors[] = Array.from(
    { length: rowCount },
    () => ({}),
  );
  if (!(error instanceof PhoneSetupValidationError))
    return { rows, general: null };
  let general: string | null = null;
  for (const fieldError of error.fieldErrors) {
    const index = fieldError.loc.find(
      (part): part is number => typeof part === "number",
    );
    const field = fieldError.loc
      .filter((part): part is string => typeof part === "string")
      .map((part) => FIELD_OF_LOC[part])
      .find((part) => part !== undefined);
    if (index !== undefined && index < rowCount && field)
      rows[index][field] ??= fieldError.msg;
    else general ??= fieldError.msg;
  }
  return { rows, general };
};

const LINE_GRID =
  "grid grid-cols-[minmax(8rem,1fr)_minmax(9rem,1fr)_7rem_auto_auto] items-start gap-x-3";

/** Saved as a full replacement, so the confirm covers the list. */
const OutboundLinesCard = ({ setup }: { setup: PhoneSetup }) => {
  const update = useUpdateOutboundLines();
  const [rows, setRows] = useState<LineDraft[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const nextKey = useRef(0);

  const draftRows = rows ?? fromSetup(setup);
  const edit = (next: LineDraft[]) => {
    update.reset();
    setRows(next);
  };
  const setRow = (key: string, patch: Partial<LineDraft>) =>
    edit(draftRows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: string) =>
    edit(draftRows.filter((r) => r.key !== key));
  const addRow = () =>
    edit([
      ...draftRows,
      {
        key: `new-${nextKey.current++}`,
        label: "",
        address: "",
        enabled: true,
        ringTimeout: String(RING_TIMEOUT_DEFAULT_SECONDS),
      },
    ]);

  const inputs = draftRows.map(toInput);
  const validation = validateOutboundLines(
    inputs,
    setup.line?.inboundDidE164 ?? null,
    toDestinationE164,
  );
  const server = from422(update.error, draftRows.length);
  const dirty = rows !== null;
  const listError = validation.list ?? server.general;

  const save = () =>
    update.mutate(inputs, {
      onSuccess: () => setRows(null),
      onSettled: () => setConfirming(false),
    });

  return (
    <SettingsCard
      title="Lines Brian rings to reach you"
      subtitle={
        setup.outboundComputerDefault
          ? "Calls start from the computer's headset; these are the clinic phones staff can pick instead if it can't connect. Pick up, press 1, and I dial the pet owner and connect you."
          : "When someone presses Call, I ring one of these lines first. Pick up, press 1, and I dial the pet owner and connect you."
      }
      data-testid="phone-lines-card"
    >
      <div className="flex flex-col gap-4">
        {draftRows.length === 0 && (
          <p
            className="text-sm text-annotation"
            data-testid="phone-lines-empty"
          >
            No lines yet — staff can't place calls until there is one to ring.
          </p>
        )}

        {draftRows.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="phone-lines-grid">
            <div
              aria-hidden="true"
              className={cn(
                LINE_GRID,
                "border-b border-rule pb-1.5 text-annotation-caps text-annotation",
              )}
            >
              <span>Name</span>
              <span>Phone number</span>
              <span>Ring for</span>
              <span className="whitespace-nowrap">Brian may ring</span>
              <span className="w-7" />
            </div>
            {draftRows.map((row, index) => {
              const errors = validation.rows[index];
              const serverErrors = server.rows[index];
              const lineName = row.label.trim() || `line ${index + 1}`;
              const toggleId = `phone-line-enabled-${row.key}`;
              // A blank, untouched field keeps quiet until it is filled in.
              const labelError =
                row.label.trim() === ""
                  ? undefined
                  : (errors.label ?? serverErrors.label);
              const addressError =
                row.address.trim() === ""
                  ? undefined
                  : (errors.address ?? serverErrors.address);
              return (
                <div
                  key={row.key}
                  className={LINE_GRID}
                  data-testid={`phone-line-${index}`}
                >
                  <Input
                    aria-label={`Name, ${lineName}`}
                    value={row.label}
                    onChange={(e) => setRow(row.key, { label: e.target.value })}
                    placeholder="Front desk"
                    error={labelError}
                    data-testid={`phone-line-label-${index}`}
                  />
                  <Input
                    aria-label={`Phone number, ${lineName}`}
                    inputMode="tel"
                    value={row.address}
                    onChange={(e) =>
                      setRow(row.key, { address: e.target.value })
                    }
                    placeholder="(555) 123-4567"
                    error={addressError}
                    data-testid={`phone-line-number-${index}`}
                  />
                  <div className="flex items-start gap-1">
                    <Input
                      aria-label={`Ring for, ${lineName}`}
                      type="number"
                      inputMode="numeric"
                      min={RING_TIMEOUT_MIN_SECONDS}
                      max={RING_TIMEOUT_MAX_SECONDS}
                      value={row.ringTimeout}
                      onChange={(e) =>
                        setRow(row.key, { ringTimeout: e.target.value })
                      }
                      className="px-2"
                      error={
                        errors.ringTimeoutSeconds ??
                        serverErrors.ringTimeoutSeconds
                      }
                      data-testid={`phone-line-ring-${index}`}
                    />
                    <span className="py-2 text-sm text-annotation">s</span>
                  </div>
                  <div className="flex h-9.5 items-center">
                    <Toggle
                      id={toggleId}
                      checked={row.enabled}
                      onCheckedChange={(enabled) =>
                        setRow(row.key, { enabled })
                      }
                      data-testid={`phone-line-enabled-${index}`}
                    />
                    <label htmlFor={toggleId} className="sr-only">
                      Brian may ring, {lineName}
                    </label>
                  </div>
                  <div className="flex h-9.5 items-center">
                    <IconButton
                      aria-label={`Remove, ${lineName}`}
                      size="sm"
                      onClick={() => removeRow(row.key)}
                      data-testid={`phone-line-remove-${index}`}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {listError && (
          <FieldError data-testid="phone-lines-list-error">
            {listError}
          </FieldError>
        )}
        {update.isError &&
          !(update.error instanceof PhoneSetupValidationError) && (
            <FieldError data-testid="phone-lines-save-error">
              Couldn't save the lines. Try again.
            </FieldError>
          )}

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={draftRows.length >= MAX_OUTBOUND_LINES}
            onClick={addRow}
            data-testid="phone-line-add"
          >
            <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
            Add line
          </Button>
          <Button
            size="sm"
            disabled={!dirty || !validation.valid}
            onClick={() => setConfirming(true)}
            data-testid="phone-lines-save"
          >
            Save
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        data-testid={confirming ? "phone-lines-confirm" : undefined}
        title="Replace the lines Brian rings?"
        confirmLabel="Save lines"
        cancelLabel="Cancel"
        busy={update.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={save}
        confirmTestId="phone-lines-confirm-confirm"
        cancelTestId="phone-lines-confirm-cancel"
      >
        <p className="text-sm text-ink-body">
          {inputs.length === 0
            ? "With no lines, staff can't place calls through Brian. Continue?"
            : "When staff press Call, I'll ring one of these lines first:"}
        </p>
        {inputs.length > 0 && (
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm text-ink">
            {inputs.map((line, i) => (
              <li key={i}>
                {line.label}{" "}
                <span className="font-mono text-annotation">
                  {formatPhoneNumber(
                    toDestinationE164(line.address) ?? line.address,
                  )}
                  {line.enabled ? "" : " · off"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </ConfirmDialog>
    </SettingsCard>
  );
};

const CallerIdCard = ({ setup }: { setup: PhoneSetup }) => {
  const sessionKey = useSessionKey();
  const start = useStartCallerIdVerification();
  const clear = useClearCallerId();
  const [typed, setTyped] = useState<string | null>(null);
  const saved = setup.callerId;
  const poll = useCallerIdVerification(sessionKey, saved.verificationPending);
  const pending = poll.data?.verificationPending ?? saved.verificationPending;
  const verified = poll.data?.verified ?? saved.verified;

  const value = typed ?? saved.numberE164 ?? "";
  const e164 = toDestinationE164(value);
  const invalid = value.trim() !== "" && e164 === null;
  const changed = e164 !== null && e164 !== saved.numberE164;

  const verify = () => {
    if (!e164) return;
    start.mutate(e164, { onSuccess: () => setTyped(null) });
  };

  const startError =
    start.error instanceof ClinicAuthError
      ? start.error.status === 503
        ? "Brian's phone provider isn't set up in this environment."
        : start.error.status === 502
          ? "The phone provider couldn't place the verification call. Try again."
          : start.error.status === 422
            ? "That isn't a number Brian can call."
            : "Couldn't start verifying. Try again."
      : start.isError
        ? "Couldn't start verifying. Try again."
        : null;

  return (
    <SettingsCard
      title="Caller ID"
      subtitle="The number pet owners see when I call them for you. It has to be verified before I can show it."
      data-testid="phone-caller-id-card"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          {saved.numberE164 === null ? (
            <span className="text-sm text-annotation">No caller ID set</span>
          ) : verified ? (
            <Swipe tone="brian" data-testid="phone-caller-id-verified">
              Verified
            </Swipe>
          ) : (
            <Swipe tone="flag" data-testid="phone-caller-id-unverified">
              {pending ? "Waiting for the press of 1" : "Unverified"}
            </Swipe>
          )}
        </div>

        {saved.numberE164 !== null && !verified && (
          <p
            className="rounded-paper bg-wash-flag/40 px-3 py-2 text-sm text-ink-body"
            data-testid="phone-caller-id-callout"
          >
            Pet owners see Brian's line until verified.
            {pending &&
              " I've asked the phone provider to call this number — pick up and press 1."}
          </p>
        )}

        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Input
              label="Clinic number to show"
              inputMode="tel"
              value={value}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="(555) 123-4567"
              error={
                invalid ? "That isn't a number Brian can call." : undefined
              }
              data-testid="phone-caller-id-number"
            />
          </div>
        </div>

        {startError && (
          <FieldError data-testid="phone-caller-id-error">
            {startError}
          </FieldError>
        )}

        <div className="flex items-center justify-between">
          {saved.numberE164 !== null ? (
            <Button
              variant="outline"
              size="sm"
              busy={clear.isPending}
              onClick={() =>
                clear.mutate(undefined, { onSuccess: () => setTyped(null) })
              }
              data-testid="phone-caller-id-clear"
            >
              Remove
            </Button>
          ) : (
            <span />
          )}
          <Button
            size="sm"
            disabled={e164 === null || (!changed && (verified || pending))}
            busy={start.isPending}
            onClick={verify}
            data-testid="phone-caller-id-verify"
          >
            {changed || saved.numberE164 === null
              ? "Save and verify"
              : "Verify again"}
          </Button>
        </div>
        <p className="text-fine-print text-annotation">
          Verifying places a call to this number; whoever answers presses 1 to
          confirm the clinic owns it.
        </p>
      </div>
    </SettingsCard>
  );
};
